import { addressSearchProps } from '@/core/utils/address-search';
import { useProvinceWards, wardLabel } from '@/app/home/components/address/useProvinceWards';
import { ConfigProvider, DatePicker, Form, Input, message, Modal, Select } from "antd";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import container from "@/lib/di/container";
import React from "react";
import { dayjsPipe } from "@/core/pipes/dayjs.pipe";
import { MODAL_THEME } from "@/core/themes/modal.theme";
import { WaterSupplySuspensionLevelRepo } from "@/core/repos/tables/water-supply-suspension-level.repo";
import { WaterSupplySuspensionLevelModel } from "@/core/models/water-supply-suspension-level.model";
import { TollAreaModel } from "@/core/models/toll-area/toll-area.model";
import { AddressRepo } from "@/core/repos/address.repo";
import { TollAreaRepo } from "@/core/repos/tables/toll-area.repo";
import { usePermissionStore } from "@/core/stores/permission.store";
import { SaveButton } from "@/app/home/components/table/SaveButton";
import { DEF_DATETIME_FORMAT } from "@/app/home/utils/global.utils";

const waterSupplySuspensionLevelRepo = container.get(WaterSupplySuspensionLevelRepo);
const addressRepo = container.get(AddressRepo);
const tollAreaRepo = container.get(TollAreaRepo);

interface DetailModalProps {
    isModalOpen: boolean,
    setIsModalOpen: Dispatch<SetStateAction<boolean>>,
    data: WaterSupplySuspensionLevelModel,
}

const INPUT_WIDTH = 450;

export const DetailModal: React.FC<DetailModalProps> = ({isModalOpen, setIsModalOpen, data}) => {
    const permissionStore = usePermissionStore();
    const [form] = Form.useForm<WaterSupplySuspensionLevelModel>();
    const { provinces, wards, loadAddress, handleProvinceChange: changeProvince, handleWardChange: changeWard } = useProvinceWards(form, addressRepo);
    const [isEdit, setIsEdit] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [messageApi, contextHolder] = message.useMessage();
    const [tollAreas, setTollAreas] = useState<TollAreaModel[]>([]);

    const areaRequest = useRef(0);

    const loadTollAreas = async (wardId: string, version: number) => {
        const nextAreas = wardId ? await tollAreaRepo.getAllByWard(wardId) : [];
        if (version === areaRequest.current) setTollAreas(nextAreas);
    };

    const handleProvinceChange = async (provinceId: string) => {
        ++areaRequest.current;
        setTollAreas([]);
        form.setFieldsValue({ tollAreaId: null });
        await changeProvince(provinceId);
    };

    const handleWardChange = async (wardId: string) => {
        const version = ++areaRequest.current;
        changeWard(wardId);
        // Clear synchronously: a delayed previous response must never widen an outage.
        form.setFieldsValue({ tollAreaId: null });
        setTollAreas([]);
        await loadTollAreas(wardId, version);
    };

    const handleDataChange = async () => {
        const version = ++areaRequest.current;
        form.setFieldsValue(data);
        setIsEdit(!!data.id);
        setTollAreas([]);
        await Promise.all([loadAddress(data), loadTollAreas(data.wardId, version)]);
    };
    useEffect(() => {
        handleDataChange();
        return () => { ++areaRequest.current; };
    }, [data]);

    const handleSave = async () => {
        try {
            setLoading(true);
            const formData = await form.validateFields();
            if (isEdit) {
                formData.id = data.id;
                const result = await waterSupplySuspensionLevelRepo.update({...data, ...formData});
                setIsModalOpen(false);
                if (result) {
                    messageApi.success({
                        content: "Đã cập nhật bản ghi"
                    });
                } else {
                    messageApi.error({
                        content: "Cập nhật bản ghi thất bại"
                    });
                }
            } else {
                const result = await waterSupplySuspensionLevelRepo.create({...data, ...formData});
                setIsModalOpen(false);
                if (result) {
                    messageApi.success({
                        content: "Thêm bản ghi mới thành công"
                    });
                } else {
                    messageApi.error({
                        content: "Thêm bản ghi mới thất bại"
                    });
                }
            }
        } catch (error) {
            console.log(error);
        }
        setLoading(false);
    };
    const handleCancel = () => {
      setIsModalOpen(false);
    };

    return (
        <ConfigProvider theme={MODAL_THEME}>
            {contextHolder}
            <Modal open={isModalOpen} onCancel={handleCancel} width={560}
                centered
                styles={{
                    mask: {
                        backdropFilter: 'blur(2px)',
                        backgroundColor: 'var(--primary-mask)'
                    }
                }}
                footer={[
                    <div key={1} className="w-full flex flex-row justify-end px-1.5">
                        <SaveButton isEdit={isEdit} loading={loading} onClick={handleSave}/>
                    </div>,
                ]}
            >
                <div className="flex flex-row w-full items-start justify-start select-none">
                    <p className="ml-2 text-2xl font-bold text-primary">THÔNG TIN CHI TIẾT</p>
                </div>
                <div className="flex flex-row justify-center mt-2">
                    <Form<WaterSupplySuspensionLevelModel>
                        name="basic"
                        className="flex flex-col items-end"
                        form={form}
                        layout="vertical"
                        disabled={isEdit&&!permissionStore.page.edit}
                    >
                        <Form.Item<WaterSupplySuspensionLevelModel>
                            name="waterSupplySuspensionId"
                            hidden
                        >
                            <Input></Input>
                        </Form.Item>
                        <Form.Item<WaterSupplySuspensionLevelModel>
                            label="Tỉnh/TP"
                            name="provinceId"
                            rules={[{ required: true, message: 'Trường này là bắt buộc!' }]}
                        >
                            <Select
                                {...addressSearchProps}
                                placeholder="Chọn tỉnh"
                                style={{width: INPUT_WIDTH}}
                                onChange={(value) => handleProvinceChange(value)}
                            >
                                {provinces.map(province => (
                                    <Select.Option key={province.id} value={province.id}>{province.name}</Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item<WaterSupplySuspensionLevelModel> name="districtId" hidden><Input /></Form.Item>
                        <Form.Item<WaterSupplySuspensionLevelModel>
                            label="Xã"
                            name="wardId"
                            rules={[{ required: true, message: 'Trường này là bắt buộc!' }]}
                        >
                            <Select
                                {...addressSearchProps}
                                placeholder="Chọn xã"
                                style={{width: INPUT_WIDTH}}
                                onChange={(value) => handleWardChange(value)}
                            >
                                {wards.map(ward => (
                                    <Select.Option key={ward.id} value={ward.id}>{wardLabel(ward)}</Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item<WaterSupplySuspensionLevelModel>
                            label="Khu vực"
                            name="tollAreaId"
                        >
                            <Select
                                allowClear
                                showSearch
                                optionFilterProp="children"
                                placeholder="Tất cả khu vực / để trống"
                                style={{width: INPUT_WIDTH}}
                            >
                                {tollAreas.map(area => (
                                    <Select.Option key={area.id} value={area.id}>{area.name}</Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item<WaterSupplySuspensionLevelModel>
                            label="Thời gian bắt đầu ngưng"
                            name="startTime"
                            getValueProps={(i) => ({value: dayjsPipe(i)})}
                            rules={[{ required: true, message: 'Trường này là bắt buộc!' }]}
                        >
                            <DatePicker format={DEF_DATETIME_FORMAT} showTime style={{width: INPUT_WIDTH}}></DatePicker>
                        </Form.Item>
                        <Form.Item<WaterSupplySuspensionLevelModel>
                            label="Thời gian cấp nước trở lại"
                            name="endTime"
                            getValueProps={(i) => ({value: dayjsPipe(i)})}
                            rules={[{ required: true, message: 'Trường này là bắt buộc!' }]}
                        >
                            <DatePicker format={DEF_DATETIME_FORMAT} showTime style={{width: INPUT_WIDTH}}></DatePicker>
                        </Form.Item>
                    </Form>
                </div>
                <div className="mb-5 mx-2 border-t border-dashed border-gray-300"></div>
            </Modal>
        </ConfigProvider>
    );
};
